import webSocketPrintService from '../api/webSocketPrint'

// Enums do Java
const PrintCommandEnum = {
  PRINT: 'PRINT',
  LIST_PRINTERS: 'LIST_PRINTERS',
  CALIBRATE: 'CALIBRATE',
  VERSION: 'VERSION',
}

const PrintStatusEnum = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  INVALID_COMMAND: 'INVALID_COMMAND',
}

class PrintWebSocketService {
  constructor() {
    this.ws = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 100
    this.isReconnecting = false
    this.connectionTimeout = null
    this.connectionMonitor = null
    this.lastUrl = null
    this.connectionPromise = null
    this.isCleaningUp = false
    this.storeDispatch = null
    this.currentPortIndex = 0
    this.availablePorts = [8181, 5050, 49200, 5000, 3000]
    this.currentPort = 8181
    this.pluginValidationTimeout = 5000
  }

  setStoreDispatch(dispatch) {
    this.storeDispatch = dispatch
  }

  startConnectionMonitor(url) {
    if (this.connectionMonitor) {
      clearInterval(this.connectionMonitor)
    }

    this.lastUrl = url
    this.connectionMonitor = setInterval(async () => {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        await this.cleanupConnection()
        this.connect(this.lastUrl)
      }
    }, 3000)
  }

  stopConnectionMonitor() {
    if (this.connectionMonitor) {
      clearInterval(this.connectionMonitor)
      this.connectionMonitor = null
    }
  }

  async cleanupConnection() {
    if (this.isCleaningUp) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return
    }

    this.isCleaningUp = true
    try {
      if (this.ws) {
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.onmessage = null
        this.ws.onopen = null
        this.ws.close(1000, 'Closing connection normally')
        this.ws = null

        // Aguarda para garantir que os recursos foram liberados
        await new Promise(resolve => setTimeout(resolve, 2000))
      }

      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout)
        this.connectionTimeout = null
      }

      if (this.connectionPromise) {
        this.connectionPromise = null
      }

      this.stopConnectionMonitor()
      this.reconnectAttempts = 0
      this.isReconnecting = false
    } finally {
      this.isCleaningUp = false
    }
  }

  async connectWithPortFallback(ports = this.availablePorts) {
    this.availablePorts = ports
    this.currentPortIndex = 0

    for (let i = 0; i < this.availablePorts.length; i++) {
      this.currentPortIndex = i
      this.currentPort = this.availablePorts[i]

      if (this.storeDispatch) {
        this.storeDispatch('setCurrentPort', this.currentPort)
        this.storeDispatch('setCurrentPortIndex', this.currentPortIndex)
      }

      try {
        const url = `ws://localhost:${this.currentPort}`
        const ws = await this.createConnection(url)
        const isValidPlugin = await this.validatePlugin()

        if (isValidPlugin) {
          this.lastUrl = url
          return ws
        } else {
          await this.cleanupConnection()
          continue
        }
      } catch (error) {
        console.error(`Falha ao conectar na porta ${this.currentPort}:`, error.message)
        await this.cleanupConnection()

        if (i < this.availablePorts.length - 1) {
          continue
        }

        if (this.storeDispatch) {
          this.storeDispatch('updateWebSocketStatus', 'disconnected')
        }

        throw new Error(
          'Não foi possível conectar em nenhuma das portas disponíveis: ' +
            this.availablePorts.join(', ')
        )
      }
    }

    throw new Error('Esgotaram-se todas as tentativas de conexão')
  }

  async validatePlugin() {
    return new Promise(resolve => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        resolve(false)
        return
      }

      let resolved = false
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true
          if (this.ws) {
            this.ws.removeEventListener('message', messageHandler)
          }

          resolve(false)
        }
      }, this.pluginValidationTimeout)

      const messageHandler = event => {
        if (!resolved) {
          resolved = true
          clearTimeout(timeout)
          if (this.ws) {
            this.ws.removeEventListener('message', messageHandler)
          }

          try {
            if (typeof event.data === 'string') {
              if (
                event.data.includes(',') ||
                event.data === 'UNKNOWN_COMMAND' ||
                event.data.startsWith('[')
              ) {
                if (this.storeDispatch) {
                  this.storeDispatch('updateWebSocketStatus', 'connected')
                }

                this.startConnectionMonitor(`ws://localhost:${this.currentPort}`)
                resolve(true)
                return
              }
            }

            const response = JSON.parse(event.data)
            if (response.command || response.status || response.printers) {
              if (this.storeDispatch) {
                this.storeDispatch('updateWebSocketStatus', 'connected')
              }

              this.startConnectionMonitor(`ws://localhost:${this.currentPort}`)
              resolve(true)
              return
            }
          } catch (e) {
            console.error('Erro ao processar resposta do plugin:', e)
          }

          resolve(false)
        }
      }

      this.ws.addEventListener('message', messageHandler)

      // Envia comando de validação (lista impressoras)
      this.sendMessage({
        command: PrintCommandEnum.LIST_PRINTERS,
        barcode: null,
        printer: null,
        label: null,
      })
    })
  }

  async tryNextPort() {
    this.currentPortIndex++

    if (this.currentPortIndex >= this.availablePorts.length) {
      throw new Error('Todas as portas foram testadas sem sucesso')
    }

    this.currentPort = this.availablePorts[this.currentPortIndex]

    if (this.storeDispatch) {
      this.storeDispatch('setCurrentPort', this.currentPort)
      this.storeDispatch('setCurrentPortIndex', this.currentPortIndex)
    }

    await this.cleanupConnection()

    const url = `ws://localhost:${this.currentPort}`
    const ws = await this.createConnection(url)

    const isValidPlugin = await this.validatePlugin()

    if (!isValidPlugin) {
      return await this.tryNextPort()
    }

    return ws
  }

  connect(url) {
    // Se uma URL específica for fornecida, usa ela diretamente (compatibilidade)
    if (url && !url.includes('undefined')) {
      return this.createConnection(url)
    }

    // Caso contrário, usa o sistema de múltiplas portas
    return this.connectWithPortFallback()
  }

  createConnection(url) {
    return new Promise(async (resolve, reject) => {
      try {
        await this.cleanupConnection()

        this.ws = new WebSocket(url)

        this.ws.onopen = () => {
          this.reconnectAttempts = 0
          this.isReconnecting = false

          resolve(this.ws)
        }

        this.ws.onclose = event => {
          if (!this.isCleaningUp && !this.isReconnecting) {
            if (this.storeDispatch) {
              this.storeDispatch('updateWebSocketStatus', 'disconnected')
            }

            // Só inicia reconexão se estava conectado anteriormente
            if (this.lastUrl && window.location.pathname.includes('print-tag')) {
              this.handleReconnect()
            }
          }
        }

        this.ws.onerror = error => {
          if (!this.isReconnecting && !this.isCleaningUp) {
            console.error('Erro WebSocket:', error)
            reject(error)
          }
        }
      } catch (error) {
        console.error('Erro ao inicializar WebSocket:', error)
        this.connectionPromise = null
        reject(error)
      }
    })
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && !this.isReconnecting) {
      this.isReconnecting = true
      this.reconnectAttempts++

      this.connectionTimeout = setTimeout(async () => {
        try {
          await this.connectWithPortFallback()
        } catch (error) {
          console.error('Falha na tentativa de reconexão:', error)
          this.isReconnecting = false

          setTimeout(() => {
            this.handleReconnect()
          }, 10000)
        }
      }, 2000 * this.reconnectAttempts)
    }
  }

  sendMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const printMessage = {
        command: message.command,
        barcode: null,
        printer: null,
        label: null,
        ...message,
      }

      this.ws.send(JSON.stringify(printMessage))
    }
  }

  getPrinters() {
    return new Promise(async (resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Não há conexão com o servidor de impressão'))
        return
      }

      let attempts = 0
      const maxAttempts = 3

      const attemptGetPrinters = async () => {
        attempts++

        const messageHandler = async event => {
          if (typeof event.data === 'string') {
            if (event.data === 'UNKNOWN_COMMAND') {
              if (this.ws) {
                this.ws.send('LIST_PRINTERS')
              }
              return
            }

            let printerList = null

            try {
              const parsed = JSON.parse(event.data)
              if (Array.isArray(parsed) && parsed.length > 0) {
                printerList = parsed
              } else if (parsed && Array.isArray(parsed.printers)) {
                printerList = parsed.printers
              }
            } catch (e) {
              const csvPrinters = event.data.split(',').filter(Boolean)
              if (csvPrinters.length) {
                printerList = csvPrinters
              }
            }

            if (printerList) {
              if (this.ws) {
                this.ws.removeEventListener('message', messageHandler)
              }
              resolve(printerList)
              return
            }

            return
          }

          if (attempts < maxAttempts) {
            if (this.ws) {
              this.ws.removeEventListener('message', messageHandler)
            }
            try {
              const newWs = await this.tryNextPort()
              if (newWs) {
                setTimeout(attemptGetPrinters, 1000)
                return
              }
            } catch (error) {
              reject(error)
              return
            }
          }

          if (this.ws) {
            this.ws.removeEventListener('message', messageHandler)
          }
          reject(new Error('Formato de resposta inválido após todas as tentativas'))
        }

        if (this.ws) {
          this.ws.addEventListener('message', messageHandler)

          this.sendMessage({
            command: PrintCommandEnum.LIST_PRINTERS,
            barcode: null,
            printer: null,
            label: null,
          })

          setTimeout(() => {
            if (this.ws) {
              this.ws.removeEventListener('message', messageHandler)
            }
            if (attempts < maxAttempts) {
              attemptGetPrinters()
            } else {
              reject(
                new Error(
                  'Tempo limite excedido ao buscar impressoras após todas as tentativas'
                )
              )
            }
          }, 5000)
        } else {
          reject(new Error('Conexão WebSocket perdida'))
        }
      }

      attemptGetPrinters()
    })
  }

  isCalibrationErrorStatus(dataStr) {
    return (
      dataStr === PrintStatusEnum.INTERNAL_ERROR ||
      dataStr === PrintStatusEnum.FAILED ||
      dataStr === PrintStatusEnum.INVALID_COMMAND
    )
  }

  isCalibrationResponse(response, printerName) {
    return (
      response &&
      (response.command === PrintCommandEnum.CALIBRATE ||
        response.printer === printerName)
    )
  }

  calibratePrinter(printerName, configLabel = null, model = null) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Não há conexão com o servidor de impressão'))
        return
      }

      const messageHandler = event => {
        let response

        try {
          response = JSON.parse(event.data)
        } catch (parseError) {
          const dataStr = event.data.toString().trim()

          if (this.isCalibrationErrorStatus(dataStr)) {
            this.ws.removeEventListener('message', messageHandler)
            reject(new Error(`Erro na calibração: ${dataStr}`))
            return
          }

          return
        }

        if (this.isCalibrationResponse(response, printerName)) {
          this.ws.removeEventListener('message', messageHandler)

          if (response.status === PrintStatusEnum.SUCCESS) {
            resolve({
              status: PrintStatusEnum.SUCCESS,
              message: response.message || 'Calibração concluída com sucesso',
              printer: printerName,
              model: model,
            })
          } else {
            const errorMessage =
              response.message || `Falha na calibração da impressora ${printerName}`
            reject(new Error(errorMessage))
          }
        }
      }

      this.ws.addEventListener('message', messageHandler)

      const command = {
        command: PrintCommandEnum.CALIBRATE,
        printer: printerName,
        label: configLabel,
        model: model,
      }

      this.sendMessage(command)

      setTimeout(() => {
        this.ws.removeEventListener('message', messageHandler)
        reject(new Error('Tempo limite excedido para calibração da impressora'))
      }, 180000)
    })
  }

  printBatchSamples(batch) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Não há conexão com o servidor de impressão'))
        return
      }

      let pendingPrints = new Map()
      let processedBarcodes = new Set()

      const messageHandler = async event => {
        try {
          const response = JSON.parse(event.data)

          if (response.status === 'SUCCESS' && response.barcode) {
            // Evita processar o mesmo código de barras mais de uma vez
            if (!processedBarcodes.has(response.barcode)) {
              const currentLabel = batch.labelCode.find(
                label => label.sample === response.barcode
              )

              if (currentLabel) {
                try {
                  await webSocketPrintService.readBarcode(currentLabel.sample)
                  processedBarcodes.add(response.barcode)
                  pendingPrints.delete(response.barcode)
                } catch (error) {
                  console.error(
                    `Erro ao marcar etiqueta ${currentLabel.sample} como impressa:`,
                    error
                  )
                }
              }
            }
          }

          // Se não há mais impressões pendentes, finaliza
          if (pendingPrints.size === 0) {
            this.ws.removeEventListener('message', messageHandler)
            resolve({ status: PrintStatusEnum.SUCCESS })
          }
        } catch (error) {
          console.error('Erro ao processar resposta:', error)
          this.ws.removeEventListener('message', messageHandler)
          reject(new Error('Formato de resposta inválido'))
        }
      }

      this.ws.addEventListener('message', messageHandler)

      // Envia os comandos de impressão
      for (const labelCode of batch.labelCode) {
        if (labelCode.sample) {
          pendingPrints.set(labelCode.sample, true)
        }

        this.sendMessage({
          command: PrintCommandEnum.PRINT,
          printer: batch.printer,
          label: labelCode.tag,
          barcode: labelCode.sample,
        })
      }

      setTimeout(() => {
        this.ws.removeEventListener('message', messageHandler)
        reject(new Error('Tempo limite excedido'))
      }, 5000 * batch.labelCode.length)
    })
  }

  async refreshPrinters() {
    if (this.storeDispatch) {
      try {
        const printers = await this.getPrinters()
        this.storeDispatch('updatePrinters', JSON.parse(printers).printers)

        // Verifica impressora padrão
        const savedPrinter = localStorage.getItem('defaultPrinter')
        if (savedPrinter && JSON.parse(printers).printers.includes(savedPrinter)) {
          this.storeDispatch('setDefaultPrinter', savedPrinter)
        }
      } catch (error) {
        console.error('Erro ao atualizar impressoras:', error)
      }
    }
  }

  async checkForNewVersion() {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Não há conexão com o servidor de impressão'))
        return
      }

      const messageHandler = event => {
        this.ws.removeEventListener('message', messageHandler)

        try {
          const response = JSON.parse(event.data)
          if (response.command === 'VERSION' && response.status === 'SUCCESS') {
            resolve({
              hasNewVersion: response.hasNewVersion,
              currentVersion: response.version,
            })
          } else {
            reject(new Error('Resposta inválida do servidor'))
          }
        } catch (e) {
          console.error('Erro ao processar resposta de versão:', e)
          reject(e)
        }
      }

      this.ws.addEventListener('message', messageHandler)

      this.sendMessage({
        command: PrintCommandEnum.VERSION,
      })
    })
  }

  async disconnect() {
    this.stopConnectionMonitor()
    await this.cleanupConnection()
  }
}

export default new PrintWebSocketService()
